<template>
  <Teleport to="body">
    <Transition name="terms-modal">
      <div
        v-if="open"
        class="terms-modal"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        @click.self="close"
      >
        <div class="terms-modal__panel">
          <header class="terms-modal__head">
            <div>
              <p class="terms-modal__meta">{{ COMPANY.serviceName }}</p>
              <h2 :id="titleId" class="terms-modal__title">{{ title }}</h2>
              <p v-if="updatedAt" class="terms-modal__date">시행일 {{ updatedAt }}</p>
            </div>
            <button type="button" class="terms-modal__close" aria-label="닫기" @click="close">
              <AppIcon icon="mdi-close" :size="22" />
            </button>
          </header>

          <div class="terms-modal__body">
            <slot />

            <div v-if="sections?.length" class="terms-modal__sections">
              <article v-for="section in sections" :key="section.title" class="terms-modal__section">
                <h3 class="terms-modal__section-title">{{ section.title }}</h3>
                <p class="terms-modal__section-body">{{ section.body }}</p>
              </article>
            </div>

            <div v-if="privacyRows?.length" class="terms-modal__table-wrap">
              <table class="terms-modal__table">
                <caption class="terms-modal__table-caption">개인정보 수집 항목</caption>
                <thead>
                  <tr>
                    <th scope="col">구분</th>
                    <th scope="col">수집 항목</th>
                    <th scope="col">수집 목적</th>
                    <th scope="col">보유 기간</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in privacyRows" :key="row.category">
                    <td>{{ row.category }}</td>
                    <td>{{ row.items }}</td>
                    <td>{{ row.purpose }}</td>
                    <td>{{ row.retention }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <footer class="terms-modal__foot">
            <button type="button" class="btn-primary terms-modal__confirm" @click="close">확인</button>
          </footer>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, onUnmounted, watch } from 'vue'
import AppIcon from '@/components/common/AppIcon.vue'
import { COMPANY } from '@/constants/legalContent'
import type { PrivacyCollectionRow } from '@/constants/legalContent'

const props = defineProps<{
  open: boolean
  title: string
  updatedAt?: string
  sections?: { title: string; body: string }[]
  privacyRows?: PrivacyCollectionRow[]
}>()

const emit = defineEmits<{
  close: []
}>()

const titleId = computed(() => `terms-modal-title-${props.title.replace(/\s/g, '-')}`)

const close = () => emit('close')

const onKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && props.open) close()
}

watch(
  () => props.open,
  (isOpen) => {
    document.body.style.overflow = isOpen ? 'hidden' : ''
  },
)

onUnmounted(() => {
  document.body.style.overflow = ''
  window.removeEventListener('keydown', onKeydown)
})

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      window.addEventListener('keydown', onKeydown)
    } else {
      window.removeEventListener('keydown', onKeydown)
    }
  },
  { immediate: true },
)
</script>

<style lang="scss" src="./TermsModal.scss"></style>
